import React, { useEffect, useState } from "react";
import { Row, Col, Input, Label, Button, Table } from "reactstrap";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import {
  fetchCustomersList,
  // fetchSessionsList,
  fetchSuppliersList,
  fetchTripsList,
  fetchVehiclesList,
} from "../../../slices/thunks";
import { fetchTaxList } from "../../../slices/TaxMaster/thunk";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Session from "../../Master/Session/Session";
import { resetTrips } from "../../../slices/Trip/reducer";

const validationSchema = Yup.object({
  // customerId will be auto-filled from selected trips (still required)
  customerId: Yup.string().required("Customer (in selected trips) is required"),
  vehicleId: Yup.string().required("Vehicle  is required"),
  tripType: Yup.string().required("Trip Type is required"),
  billDate: Yup.date().required("Bill date is required"),
  tripIds: Yup.array().min(1, "Select at least one trip"),
  // selectedTaxId: Yup.string().required("Please select tax"),
});

const GenerateBillForm = ({ onSubmit, onCloseForm }) => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // totals stored in local state
  const [totals, setTotals] = useState({
    subTotal: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalGst: 0,
    grandTotal: 0,
  });

  const selector = createSelector(
    (state) => state,
    (state) => ({
      trips: state.Trip.trips,
      loading: state.Trip.loading,
      taxes: state.TaxMaster.taxes,
      customersList: state.Customer.customers,
      vehicles: state.Vehicle.vehicles,

      suppliers: state.Supplier.suppliers,
      activeSessions: state.Session.selectedSessionYear,
    })
  );

  const {
    trips,
    loading,
    taxes,
    suppliers,
    activeSessions,
    customersList,

    vehicles,
  } = useSelector(selector);

  const tripOptions = [
    { value: "KM", label: "OutStation" },
    { value: "perTrip", label: "Local" },
  ];

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // fetch data
  useEffect(() => {
    // dispatch(
    //   fetchTripsList({
    //     pageNo,
    //     pageSize,
    //     isPagination: 1,
    //     sortBy: "createdAt",
    //     sortOrder: "DESC",
    //     isBill: false,
    //   })
    // );

    dispatch(resetTrips());

    dispatch(
      fetchTaxList({
        isPagination: 0,
      })
    );
    dispatch(
      fetchVehiclesList({
        isPagination: 0,
      })
    );

    dispatch(
      fetchCustomersList({
        isPagination: 0,
      })
    );
    // dispatch(
    //   fetchSessionsList({
    //     isPagination: 0,
    //     isActive: true,
    //   })
    // );
    dispatch(
      fetchSuppliersList({
        isPagination: 0,
      })
    );
  }, [dispatch]);

  // const calculateTotals = (selectedTripIds = [], selectedTaxId = "") => {
  //   const selectedTrips = trips.filter((t) => selectedTripIds.includes(t.id));
  //   const subTotal = selectedTrips.reduce(
  //     (sum, t) => sum + (Number(t.amount) || 0),
  //     0
  //   );

  //   const selectedTax = taxes.find((t) => t.id === selectedTaxId);

  //   let cgst = 0,
  //     sgst = 0,
  //     igst = 0;

  //   if (selectedTax) {
  //     if (selectedTax.taxType === "C&SGST") {
  //       cgst = (subTotal * Number(selectedTax.cgst || 0)) / 100;
  //       sgst = (subTotal * Number(selectedTax.sgst || 0)) / 100;
  //     } else if (selectedTax.taxType === "IGST") {
  //       igst = (subTotal * Number(selectedTax.taxPerc || 0)) / 100;
  //     }
  //   }

  //   const totalGst = cgst + sgst + igst;
  //   const grandTotal = subTotal + totalGst;

  //   setTotals({
  //     subTotal,
  //     cgst,
  //     sgst,
  //     igst,
  //     totalGst,
  //     grandTotal,
  //   });
  // };

  const calculateTotals = (
    selectedTripIds = [],
    selectedTaxId = "",
    includeCharges = false
  ) => {
    const selectedTrips = trips.filter((t) => selectedTripIds.includes(t.id));

    // Subtotal = ONLY trip amount
    let subTotal = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0
    );

    // EXTRA CHARGES (NO GST APPLIED ON THEM)
    let toll = 0,
      handling = 0,
      other = 0;

    if (includeCharges) {
      toll = selectedTrips.reduce(
        (s, t) => s + (Number(t.tollCharges) || 0),
        0
      );
      handling = selectedTrips.reduce(
        (s, t) => s + (Number(t.handlingCharges) || 0),
        0
      );
      other = selectedTrips.reduce(
        (s, t) => s + (Number(t.otherCharges) || 0),
        0
      );
    }

    const extraCharges = toll + handling + other;

    // GST ONLY on subTotal
    const selectedTax = taxes.find((t) => t.id === selectedTaxId);
    let cgst = 0,
      sgst = 0,
      igst = 0;

    if (selectedTax) {
      if (selectedTax.taxType === "C&SGST") {
        cgst = (subTotal * Number(selectedTax.cgst || 0)) / 100;
        sgst = (subTotal * Number(selectedTax.sgst || 0)) / 100;
      } else if (selectedTax.taxType === "IGST") {
        igst = (subTotal * Number(selectedTax.taxPerc || 0)) / 100;
      }
    }

    const totalGst = cgst + sgst + igst;

    // GRAND TOTAL = trip amount + gst + extra charges (NO GST ON CHARGES)
    const grandTotal = subTotal + totalGst + extraCharges;

    setTotals({
      subTotal,
      toll,
      handling,
      other,
      extraCharges,
      cgst,
      sgst,
      igst,
      totalGst,
      grandTotal,
    });
  };

  const taxOptions = (taxList) =>
    (taxList || []).map((t) => ({
      label: `${t.name?.toUpperCase()} (${t.taxType})`,
      value: t.id,
    }));

  return (
    <div className="p-3">
      <Formik
        initialValues={{
          customerId: "",
          customerAddress: "",
          customerGSTNo: "",
          billDate: new Date(),
          tripIds: [],
          tripType: "",
          selectedTaxId: "",
          includeCharges: false,
          selectedTaxId: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          const getPartyType = (tripType) => {
            if (tripType === "KM") return "OUTSTATION";
            if (tripType === "perTrip") return "LOCAL";
            return null;
          };

          const selectedTripsData = trips.filter((t) =>
            values.tripIds.includes(t.id)
          );

          const totalKmTrip = selectedTripsData.reduce(
            (sum, t) => sum + (Number(t.kmOrTrip) || 0),
            0
          );

          // const totalKmTripRate = selectedTripsData.reduce(
          //   (sum, t) => sum + (Number(t.rate) || 0),
          //   0
          // );

          const totalKmTripRate = selectedTripsData[0]?.rate || 0;

          const d = values.billDate;
          const formattedBillDate =
            ("0" + d.getDate()).slice(-2) +
            "-" +
            ("0" + (d.getMonth() + 1)).slice(-2) +
            "-" +
            d.getFullYear();
          const payload = {
            sessionId: trips?.[0]?.sessionId || null,
            taxId: values.selectedTaxId || null,
            billDate: formattedBillDate,
            customerId: values.customerId,
            customerAddress: values.customerAddress || "",
            customerGSTNo: values.customerGSTNo || "",
            totalAmount: Number(totals.subTotal || 0),
            cgst: Number(totals.cgst || 0),
            sgst: Number(totals.sgst || 0),
            igst: Number(totals.igst || 0),
            totalGst: Number(totals.totalGst || 0),
            subTotal: Number(totals.subTotal || 0),
            grandTotal: Number(totals.grandTotal || 0),
            tollCharges: values.includeCharges ? totals.toll : null,
            handlingCharges: values.includeCharges ? totals.handling : null,
            otherCharges: values.includeCharges ? totals.other : null,
            kmOrTrip: totalKmTrip || 0,
            rate: totalKmTripRate || 0,

            partyType: getPartyType(values.tripType),
            // note: values.note || "",

            billInvoiceRequest: values.tripIds.map((id) => ({
              dailyTripId: id,
            })),
          };

          if (onSubmit) onSubmit(payload);  
          resetForm();
          dispatch(resetTrips());
          onCloseForm();
        }}
      >
        {({ values, setFieldValue, setFieldError, setFieldTouched }) => {
          // Handler used for trip checkbox changes
          const handleTripToggle = (tripId, checked) => {
            // update tripIds
            const current = Array.isArray(values.tripIds)
              ? [...values.tripIds]
              : [];
            let updated;
            if (checked) {
              updated = current.includes(tripId)
                ? current
                : [...current, tripId];
            } else {
              updated = current.filter((id) => id !== tripId);
            }

            // set Formik tripIds
            setFieldValue("tripIds", updated);

            // Recalculate totals
            calculateTotals(
              updated,
              values.selectedTaxId,
              values.includeCharges
            );

            const selectedTrips = trips.filter((t) => updated.includes(t.id));
            const uniqueCustomerIds = [
              ...new Set(
                selectedTrips.map((t) => t.customerId).filter(Boolean)
              ),
            ];

            if (uniqueCustomerIds.length === 1) {
              const custId = uniqueCustomerIds[0];

              setFieldValue("customerId", custId);

              const customerObj = customersList.find((c) => c.id === custId);

              setFieldValue("customerAddress", customerObj?.address || "");
              setFieldValue("customerGSTNo", customerObj?.gstnNo || "");
              // clear any tripIds error about multiple customers
              setFieldError("tripIds", undefined);
            } else if (uniqueCustomerIds.length === 0) {
              // no trips selected
              setFieldValue("customerId", "");
              setFieldValue("customerAddress", "");
              setFieldValue("customerGSTNo", "");
              setFieldError("tripIds", undefined);
            } else {
              // multiple different customers selected -> invalid state
              setFieldValue("customerId", "");
              setFieldValue("customerAddress", "");
              setFieldValue("customerGSTNo", "");
              setFieldError(
                "tripIds",
                "Selected trips belong to multiple customers. Please pick trips for a single customer."
              );
            }
          };

          // SELECT ALL — adds/removes all trip IDs
          const handleSelectAll = (checked) => {
            // ALL trip IDs from the current list
            const allIds = checked ? trips.map((t) => t.id) : [];

            // Update Formik state
            setFieldValue("tripIds", allIds);

            // Recalculate totals
            calculateTotals(
              allIds,
              values.selectedTaxId,
              values.includeCharges
            );

            // Auto-select customer if needed
            const selectedTrips = trips.filter((t) => allIds.includes(t.id));
            const uniqueCustomerIds = [
              ...new Set(selectedTrips.map((t) => t.customerId)),
            ];

            if (uniqueCustomerIds.length === 1) {
              const custId = uniqueCustomerIds[0];
              const cust = customersList.find((c) => c.id === custId);

              setFieldValue("customerId", custId);
              setFieldValue("customerAddress", cust?.address || "");
              setFieldValue("customerGSTNo", cust?.gstnNo || "");
            } else {
              setFieldValue("customerId", "");
              setFieldValue("customerAddress", "");
              setFieldValue("customerGSTNo", "");
            }
          };

          // Handler when tax is changed
          const handleTaxChange = (opt) => {
            const newTaxId = opt?.value || "";
            setFieldValue("selectedTaxId", newTaxId);
            // recalc totals with current trips & new tax
            calculateTotals(
              values.tripIds || [],
              newTaxId,
              values.includeCharges
            );
          };

          return (
            <Form>
              <Row className="g-4">
                {/* Bill Date */}

                <Col md={3}>
                  {/* <Label>Session Year</Label> */}

                  {/* <Select
                    options={session?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={(opt) => {
                      const sessionId = opt ? opt.value : null;

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          sessionId: sessionId,
                          vehicleId: values.vehicleId,
                          pageNo: 0,
                          isBill: false,
                        })
                      );

                      setFieldValue("sessionIds", []);
                      calculateTotals([], values.selectedTaxId);
                    }}
                    placeholder="Select Session Year"
                    isClearable
                  /> */}

                  <div>
                    <label className="form-label">Session *</label>

                    {/* Auto-set Formik value (no useEffect) */}
                    {activeSessions &&
                      values.sessionId !== activeSessions?.id &&
                      setFieldValue("sessionId", activeSessions?.id)}

                    {/* Readonly input to show session name */}
                    <input
                      type="text"
                      className="form-control"
                      value={activeSessions?.name || ""}
                      disabled
                    />

                    {/* Hidden Formik input */}
                    <input
                      type="hidden"
                      name="sessionId"
                      value={values.sessionId}
                    />

                    {/* {touched.sessionId && errors.sessionId && (
                      <div className="text-danger small">
                        {errors.sessionId}
                      </div>
                    )} */}
                  </div>
                </Col>

                {/* <Col md={4}>
                  <Label>Customer (from selected trips)</Label>
                  <Input
                    value={
                      values.customerId
                        ? customersList.find((c) => c.id === values.customerId)
                            ?.name || ""
                        : ""
                    }
                    placeholder="Select Customer"
                  />
                </Col> */}

                <Col md={3}>
                  <Label>
                    Customer <span className="text-danger">*</span>
                  </Label>

                  <Select
                    options={customersList.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    value={
                      values.customerId
                        ? {
                            value: values.customerId,
                            label: customersList.find(
                              (c) => c.id === values.customerId
                            )?.name,
                          }
                        : null
                    }
                    onChange={(opt) => {
                      // setFieldValue("customerId", opt.value);
                      setFieldValue("customerId", opt?.value);
                      // Call backend API to filter trips by customer
                      dispatch(
                        fetchTripsList({
                          customerId: opt?.value,
                          sessionId: values?.sessionId,

                          isBill: false,
                        })
                      );

                      // Clear previously selected trips
                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        values.includeCharges
                      );
                    }}
                    placeholder="Select Customer"
                  />

                  <ErrorMessage
                    name="customerId"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                <Col md={3}>
                  <Label>
                    Vehicle <span className="text-danger">*</span>
                  </Label>

                  <Select
                    name="vehicleId"
                    options={vehicles?.map((v) => ({
                      value: v.id,
                      label: v.vehicleNo,
                    }))}
                    onChange={(opt) => {
                      const vehicleId = opt ? opt.value : null;
                      setFieldValue("vehicleId", vehicleId);

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: vehicleId,
                          sessionId: values.sessionId,
                          isBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        values.includeCharges
                      );
                    }}
                    onBlur={() => setFieldTouched("vehicleId", true)}
                    placeholder="Select Vehicle"
                  />
                  <ErrorMessage
                    name="vehicleId"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                <Col md={3}>
                  <Label>
                    Trip Type <span className="text-danger">*</span>
                  </Label>

                  <Select
                    options={tripOptions}
                    value={
                      tripOptions.find(
                        (item) => item.value === values.tripType
                      ) || null
                    }
                    onChange={(opt) => {
                      setFieldValue("tripType", opt?.value);

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId: values.supplierId,
                          sessionId: values.sessionId,
                          tripType: opt.value,
                          isBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        values.includeCharges
                      );
                    }}
                    onBlur={() => setFieldTouched("tripType", true)}
                    placeholder="Select Trip Type"
                  />
                  <ErrorMessage
                    name="tripType"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                <Col md={3}>
                  <Label>Supplier</Label>

                  <Select
                    options={suppliers?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={(opt) => {
                      const supplierId = opt ? opt.value : null;
                      setFieldValue("supplierId", supplierId);

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId: supplierId,

                          isBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        values.includeCharges
                      );
                    }}
                    placeholder="Select Vehicle"
                    isClearable
                  />
                </Col>

                <Col md={3}>
                  <Label>
                    Bill Date <span className="text-danger">*</span>
                  </Label>

                  <Flatpickr
                    className="form-control"
                    placeholder="Select Bill Date"
                    value={values.billDate}
                    onChange={(dates) => {
                      setFieldValue("billDate", dates[0]);
                    }}
                    options={{
                      defaultDate: new Date(),
                      dateFormat: "d-m-Y",
                    }}
                  />

                  <ErrorMessage
                    name="billDate"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                <Col md={3}>
                  <Label>From Date</Label>

                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select From Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.fromDate || ""}
                      onChange={(date) => {
                        const fromDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;

                        setFieldValue("fromDate", fromDateValue);

                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            supplierId: values.supplierId,
                            fromDate: fromDateValue,
                            toDate: values.toDate,

                            isBill: false,
                          })
                        );

                        calculateTotals(
                          values.sessionIds,
                          values.selectedTaxId,
                          values.includeCharges
                        );
                      }}
                    />

                    {/* Calendar Icon Button */}
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <Label>To Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select To Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.toDate}
                      onChange={(date) => {
                        const toDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;

                        setFieldValue("toDate", toDateValue);

                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            fromDate: values.fromDate,
                            supplierId: values.supplierId,
                            toDate: toDateValue,

                            isBill: false,
                          })
                        );

                        calculateTotals(
                          values.sessionIds,
                          values.selectedTaxId,
                          values.includeCharges
                        );
                      }}
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                {/* TRIP LIST */}
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center">
                    <Label>Select Trips *</Label>

                    {/* <div style={{ width: "285px" }}>
                      <Label className="mb-0">Note:</Label>
                      <Input
                        type="text"
                        value={values.note}
                        onChange={(e) => setFieldValue("note", e.target.value)}
                        placeholder="Enter note"
                      />
                    </div> */}
                  </div>
                  <Table bordered responsive className="mt-2">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>
                          <Input
                            type="checkbox"
                            checked={
                              values.tripIds.length > 0 &&
                              values.tripIds.length === trips.length
                            }
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </th>

                        <th>Customer</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Rate</th>
                        <th>KM/Trip</th>
                        <th>Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan="9" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      )}

                      {!loading && trips.length === 0 && (
                        <tr>
                          <td
                            colSpan="9"
                            className="text-center text-muted py-4"
                          >
                            <i className="ri-emotion-unhappy-line fs-2 d-block mb-1"></i>
                            No Record found.
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        trips.map((trip) => {
                          const checked = (values.tripIds || []).includes(
                            trip.id
                          );
                          return (
                            <tr key={trip.id}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    handleTripToggle(trip.id, e.target.checked)
                                  }
                                />
                              </td>

                              <td>{trip.customerName}</td>
                              <td>{trip.suppliersName}</td>
                              <td>{trip.tripDate}</td>
                              <td>{trip.vehicleNo}</td>
                              <td>{trip.rate}</td>
                              <td>{trip.kmOrTrip}</td>
                              <td>{trip.amount}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>

                  <ErrorMessage
                    name="tripIds"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                {/* BILL SUMMARY (single-row layout) */}

                {/* <Col md={4}>
                  <Label>Tax *</Label>
                  <Select
                    options={taxOptions(taxes)}
                    value={
                      values.selectedTaxId
                        ? {
                            value: values.selectedTaxId,
                            label:
                              taxes.find((t) => t.id === values.selectedTaxId)
                                ?.name +
                              ` (${
                                taxes.find((t) => t.id === values.selectedTaxId)
                                  ?.taxType
                              })`,
                          }
                        : null
                    }
                    onChange={handleTaxChange}
                    placeholder="Select Tax"
                  />
                  <ErrorMessage
                    name="selectedTaxId"
                    component="div"
                    className="text-danger small"
                  />
                </Col> */}

                <Col md={12} className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Bill Summary</h5>

                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="includeCharges"
                        checked={values.includeCharges}
                        onChange={(e) => {
                          setFieldValue("includeCharges", e.target.checked);

                          // Recalculate totals with updated toggle
                          calculateTotals(
                            values.tripIds,
                            values.selectedTaxId,
                            e.target.checked
                          );
                        }}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="includeCharges"
                      >
                        Include Toll / Handling / Other Charges
                      </Label>
                    </div>

                    <div style={{ width: "280px" }}>
                      <Label className="mb-1">Tax *</Label>
                      <Select
                        options={taxOptions(taxes)}
                        value={
                          values.selectedTaxId
                            ? {
                                value: values.selectedTaxId,
                                label:
                                  taxes.find(
                                    (t) => t.id === values.selectedTaxId
                                  )?.name +
                                  ` (${
                                    taxes.find(
                                      (t) => t.id === values.selectedTaxId
                                    )?.taxType
                                  })`,
                              }
                            : null
                        }
                        onChange={handleTaxChange}
                        placeholder="Select Tax"
                      />
                      <ErrorMessage
                        name="selectedTaxId"
                        component="div"
                        className="text-danger small"
                      />
                    </div>
                  </div>

                  <Table bordered>
                    <thead>
                      <tr>
                        <th>Sub Total</th>

                        {/* GST Columns */}
                        {(() => {
                          const selectedTax = taxes.find(
                            (t) => t.id === values.selectedTaxId
                          );
                          if (selectedTax?.taxType === "C&SGST") {
                            return (
                              <>
                                <th>CGST ({selectedTax.cgst}%)</th>
                                <th>SGST ({selectedTax.sgst}%)</th>
                              </>
                            );
                          }
                          if (selectedTax?.taxType === "IGST") {
                            return <th>IGST ({selectedTax.taxPerc}%)</th>;
                          }
                          return null;
                        })()}

                        {/* Charges columns appear BEFORE Total GST */}
                        {values.includeCharges && <th>Toll Charges</th>}
                        {values.includeCharges && <th>Handling Charges</th>}
                        {values.includeCharges && <th>Other Charges</th>}
                        {values.includeCharges && <th>Total Extra Charges</th>}

                        {/* TOTAL GST should be SECOND LAST */}
                        <th>Total GST</th>

                        {/* LAST COLUMN */}
                        <th>Grand Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>₹ {Number(totals.subTotal || 0).toFixed(2)}</td>

                        {/* GST Values */}
                        {(() => {
                          const selectedTax = taxes.find(
                            (t) => t.id === values.selectedTaxId
                          );
                          if (selectedTax?.taxType === "C&SGST") {
                            return (
                              <>
                                <td>₹ {Number(totals.cgst || 0).toFixed(2)}</td>
                                <td>₹ {Number(totals.sgst || 0).toFixed(2)}</td>
                              </>
                            );
                          }
                          if (selectedTax?.taxType === "IGST") {
                            return (
                              <td>₹ {Number(totals.igst || 0).toFixed(2)}</td>
                            );
                          }
                          return null;
                        })()}

                        {/* Charges Values BEFORE Total GST */}
                        {values.includeCharges && (
                          <>
                            <td>₹ {Number(totals.toll || 0).toFixed(2)}</td>
                            <td>₹ {Number(totals.handling || 0).toFixed(2)}</td>
                            <td>₹ {Number(totals.other || 0).toFixed(2)}</td>
                            <td>
                              <b>
                                ₹ {Number(totals.extraCharges || 0).toFixed(2)}
                              </b>
                            </td>
                          </>
                        )}

                        {/* SECOND LAST */}
                        <td>
                          <b>₹ {Number(totals.totalGst || 0).toFixed(2)}</b>
                        </td>

                        {/* LAST */}
                        <td>
                          <b>₹ {Number(totals.grandTotal || 0).toFixed(2)}</b>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>

                {/* SUBMIT */}
                <Col md={12} className="text-end">
                  <Button color="success" className="btn-sm" type="submit">
                    Generate Bill
                  </Button>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default GenerateBillForm;
